import Image from "@/components/Atoms/Image";
import { LazyMotion, domAnimation, m } from "framer-motion";
import { getPortfolioSlug, pageRelatedLink, slideIn } from "@/lib/functions";
import { routerHandler } from "@/lib/routes/router-links-handler";
import { useRouter } from "next/router";
import { ButtonMaster } from "@/components/Atoms/Buttons";

import {  MultiPurposeTitle } from "@/components/Atoms/Title";
import { MultiPurposeParagraph } from "@/components/Atoms/Paragraph";
import { CustomUnderline, Underline } from "@/components/Atoms/Underline";
import CustomImage from "@/components/Atoms/Image";

interface Props {
  title: string;
  description: string;
  label: any;
  color: any[any];
  layout:
    | "center"
    | "left"
    | "right"
    | "fixedCenter"
    | "default"
    | "featuredCenter"
    | null
    | undefined;
  cta?: any;
  flex: string;
  underline: any;
  hasSpacer?: boolean;
  /** When true, parent supplies the container (e.g. hero slider reduced layout). */
  embedded?: boolean;
}

/** Spacer shape is absolute — use padding on copy + header so text clears the overlap on mobile. */
const heroSpacerCopyPadding = "max-lg:!pb-40 lg:!pb-[5rem]";
const heroSpacerHeaderSpacing = "max-lg:mb-24";

function heroLabelAlign(
  layout?: string | null
): "center" | "left" | "right" {
  if (
    layout === "center" ||
    layout === "fixedCenter" ||
    layout === "featuredCenter"
  ) {
    return "center";
  }
  if (layout === "right") return "right";
  return "left";
}

function HeroLabel({
  data,
  color,
  layout,
}: {
  data?: string;
  color?: any;
  layout?: string | null;
}) {
  if (!data) return null;

  return (
    <MultiPurposeParagraph
      type="label"
      data={data}
      color={color}
      align={heroLabelAlign(layout)}
    />
  );
}

export function SliderCopyCard({
  title,
  description,
  label,
  color,
  layout,
  cta,
  flex,
  underline,
  hasSpacer,
  embedded = false,
}: Props) {
  const { locale }: any = useRouter();

  return (
    <>
      <div
        className={` ${
          flex == "center"
            ? "items-center"
            : flex == "start"
            ? "items-start"
            : "items-end"
        } ${
          layout == "left"
            ? "justify-start"
            : layout == "right"
            ? "justify-end"
            : layout == "center"
            ? "justify-center"
            : ""
        } ${
          embedded
            ? "flex relative z-10 w-full h-full px-0"
            : "container mx-auto flex relative z-10 h-full"
        }`}
      >
        <header
          className={`flex flex-col gap-4 pt-12 pb-24 lg:py-24 ${
            hasSpacer ? `${heroSpacerCopyPadding} ${heroSpacerHeaderSpacing}` : ""
          }`}
        >
          <div
            className={`${
              embedded ? "w-full" : ""
            } w-fit flex-none`}
          >
            <LazyMotion features={domAnimation}>
              <m.div
                variants={slideIn()}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className={`${
                  layout === "center" ? "items-center" : ""
                } flex flex-col gap-12`}
              >
                <div className="flex flex-col gap-2 md:gap-6 items-start max-w-3xl">
                  {label[0] && (
                    <HeroLabel data={label[1]} color={color} layout={layout} />
                  )}
                  {title && (
                    <div className="flex flex-col">
                      <MultiPurposeTitle
                        tag="h2"
                        data={title}
                        color={color}
                        type="featured"
                        layout={layout}
                      />
                      {underline?.length > 0 && (
                        <>
                          {underline[1]?.img ? (
                            <CustomUnderline
                              shape={underline[1]?.img}
                              color={underline[2]?.color}
                            />
                          ) : (
                            <Underline color={underline[2]?.color} />
                          )}
                        </>
                      )}
                    </div>
                  )}
                  {description && (
                    <MultiPurposeTitle
                      tag={"p"}
                      data={description}
                      color={color}
                      type="headline"
                      layout={"left"}
                    />
                  )}
                  {!label[0] && (
                    <HeroLabel data={label[1]} color={color} layout="left" />
                  )}
                </div>

                {cta[0] && (
                  <>
                    {cta[1].map((entry: any, i: number) => {
                      const portfolioSlug = getPortfolioSlug(
                        entry.portfolioConfiguration,
                        locale
                      );
                      if (entry.linksToProductPage) {
                        return (
                          <ButtonMaster
                            key={i}
                            type={entry?.buttonStyle}
                            cta={entry?.typeColor?.color}
                            color={entry?.labelColor?.color}
                            customIcon={entry?.icon?.img}
                            customColor={entry?.iconColor?.color}
                            link={`${routerHandler({
                              typename: entry?.product.product.__typename,
                              slug: [
                                portfolioSlug,
                                entry?.product.product.productTypeSpecific
                                  ?.slug,
                                entry?.product.product.slug,
                              ],
                              locale: locale,
                            })}`}
                          >
                            {entry?.label || entry?.buttonLabel?.label}
                          </ButtonMaster>
                        );
                      } else {
                        return (
                          <ButtonMaster
                            key={i}
                            type={entry?.buttonStyle}
                            cta={entry?.typeColor?.color}
                            color={entry?.labelColor?.color}
                            customIcon={entry?.icon?.img}
                            customColor={entry?.iconColor?.color}
                            link={routerHandler({
                              typename:
                                entry?.page?.__typename === "ArticleRecord"
                                  ? "ArticleRecord"
                                  : entry?.__typename,
                              slug: pageRelatedLink(entry?.page, locale),
                              locale: locale,
                            })}
                          >
                            {entry?.label || entry?.buttonLabel?.label}
                          </ButtonMaster>
                        );
                      }
                    })}
                  </>
                )}
              </m.div>
            </LazyMotion>
          </div>
        </header>
      </div>
    </>
  );
}

interface ImageCopyCardProps extends Props {
  swapTitle: boolean;
  imageReplacer: any;
  width?: string;
  labelPosition?: boolean;
  imagePosition?: string;
  underline: any;
  labelColor?: any;
  className?: any;
  hasSpacer?: boolean;
}

export function ImageCopyCard({
  title,
  description,
  label,
  color,
  swapTitle,
  imageReplacer,
  labelPosition,
  layout,
  width,
  imagePosition,
  underline,
  labelColor,
  className,
  hasSpacer,
}: ImageCopyCardProps) {
  return (
    <>
      <div
        className={`  ${
          layout == "left"
            ? "justify-start text-left"
            : "justify-center text-center "
        } container mx-auto flex relative z-10 md:px-12 lg:px-16 h-fit pb-[5rem] pt-[100px] ${
          hasSpacer ? heroSpacerCopyPadding : ""
        } ${className}`}
      >
        {imagePosition == "featured" ? (
          <header
            className={` flex flex-col md:gap-4 items-center  w-full  ${
              width === "full" ? "w-full" : "lg:max-w-[50rem]"
            } ${layout == "left" ? "" : " lg:pb-6"} ${
              hasSpacer ? heroSpacerHeaderSpacing : ""
            }`}
          >
            <Image className={`w-full max-w-[30rem]`} data={imageReplacer} />
            <div className="lg:max-w-[50rem]">
              {description && (
                <MultiPurposeTitle
                  tag={"p"}
                  data={description}
                  color={color}
                  type="headline"
                  layout={"left"}
                />
              )}
            </div>
            {!labelPosition && (
              <HeroLabel
                data={label}
                color={labelColor || color}
                layout={layout}
              />
            )}
          </header>
        ) : (
          <header
            className={` flex flex-col gap-2 lg:gap-8  w-full ${
              layout == "left"
                ? "items-start"
                : layout == "fixedCenter"
                ? "items-center"
                : layout == "center"
                ? "items-center"
                : "items-end"
            } ${width === "full" ? "w-full" : "lg:max-w-[50rem]"} ${
              layout == "left" ? "" : " lg:pb-6"
            } ${hasSpacer ? heroSpacerHeaderSpacing : ""}`}
          >
            {swapTitle && (
              <>
                {imageReplacer && (
                  <Image
                    className={`${
                      imageReplacer?.responsiveImage?.aspectRatio > 1
                        ? "max-w-[15rem] md:max-w-[25rem] lg:max-w-[35rem]"
                        : `${
                            imageReplacer?.format == "svg"
                              ? `max-w-[6rem]`
                              : `max-w-[25rem]`
                          }`
                    }`}
                    data={imageReplacer.url}
                  />
                )}
              </>
            )}
            {labelPosition && (
              <HeroLabel
                data={label}
                color={labelColor || color}
                layout={layout}
              />
            )}
            {swapTitle ? (
              <>
                {!imageReplacer && (
                  <MultiPurposeTitle
                    tag={`h2`}
                    data={description}
                    color={color}
                    type="featured"
                    layout={layout}
                  />
                )}
              </>
            ) : (
              <div className="flex flex-col w-fit">
                <MultiPurposeTitle
                  tag="h2"
                  data={title}
                  color={color}
                  type="featured"
                  layout={layout}
                />
                {underline && (
                  <>
                    {underline[1]?.img ? (
                      <CustomUnderline
                        shape={underline[1]?.img}
                        color={underline[2]?.color}
                      />
                    ) : (
                      <Underline color={underline[2]?.color} />
                    )}
                  </>
                )}
              </div>
            )}
            {description && (
              <div className="lg:max-w-[50rem]">
                <MultiPurposeTitle
                  tag={"p"}
                  data={description}
                  color={color}
                  type="excerptAlt"
                  layout={layout}
                />
              </div>
            )}

            {!labelPosition && (
              <HeroLabel
                data={label}
                color={labelColor || color}
                layout={layout}
              />
            )}
          </header>
        )}
      </div>
    </>
  );
}

interface FeaturedProps {
  title: string;
  description: string;
  descriptionColor: any;
  color: any[any];
  cta: any;
  parallaxImage?: any;
  hasSpacer?: boolean;
  label?: [boolean, string | undefined];
  layout?:
    | "center"
    | "left"
    | "right"
    | "fixedCenter"
    | "default"
    | "featuredCenter"
    | null
    | undefined;
  embedded?: boolean;
  underline?: any;
}

export function FeaturedCopyCard({
  title,
  description,
  color,
  descriptionColor,
  cta,
  parallaxImage,
  hasSpacer,
  label,
  layout = "fixedCenter",
  embedded = false,
  underline,
}: FeaturedProps) {
  const { locale }: any = useRouter();
  const copyLayout = embedded && layout === "right" ? "left" : layout;
  const titleLayout = embedded ? copyLayout : "fixedCenter";
  const headerAlign =
    copyLayout === "left"
      ? "items-start text-left"
      : copyLayout === "right"
        ? "items-end text-right"
        : "items-center text-center";

  return (
    <header
      className={`flex flex-col md:gap-4 w-full ${
        embedded ? "px-0" : "px-4"
      } ${headerAlign} ${hasSpacer ? heroSpacerHeaderSpacing : ""}`}
    >
      <div
        className={`relative flex flex-col gap-4 md:gap-6 ${headerAlign} ${
          embedded ? "w-full" : "pt-6 lg:pt-0 lg:pb-12"
        }`}
      >
        {label?.[0] && label?.[1] && (
          <HeroLabel data={label[1]} color={color} layout={copyLayout} />
        )}
        <div className="relative">
          <MultiPurposeTitle
            type="featured"
            layout={titleLayout}
            color={color}
            tag="h2"
            data={title}
          />
          {!embedded && underline?.length > 0 && (
            <>
              {underline[1]?.img ? (
                <CustomUnderline
                  shape={underline[1]?.img}
                  color={underline[2]?.color}
                />
              ) : (
                <Underline color={underline[2]?.color} />
              )}
            </>
          )}
          {!embedded && parallaxImage?.[0] && (
            <div className="absolute left-0  translate-x-[-2rem] md:translate-x-[-3rem] bottom-0 top-0 flex justify-center">
              <CustomImage
                className="max-w-[1.5rem] md:max-w-[3rem]"
                data={parallaxImage[1]}
              />
            </div>
          )}
          {!embedded && parallaxImage?.[0] && (
            <div className="absolute right-0 translate-x-[2rem]  md:translate-x-[3rem] bottom-0 top-0 flex justify-center">
              <CustomImage
                className="max-w-[1.5rem] md:max-w-[3rem]"
                data={parallaxImage[1]}
              />
            </div>
          )}
        </div>
        {embedded && underline?.length > 0 && (
          <>
            {underline[1]?.img ? (
              <CustomUnderline
                shape={underline[1]?.img}
                color={underline[2]?.color}
              />
            ) : (
              <Underline color={underline[2]?.color} />
            )}
          </>
        )}
        {description && (
          <MultiPurposeTitle
            type="featured"
            layout={titleLayout}
            color={descriptionColor}
            tag="h3"
            data={description}
          />
        )}
        {cta[0] && (
          <>
            {cta[1].map((entry: any, i: number) => {
              if (entry.linksToProductPage) {
                const portfolioSlug = getPortfolioSlug(entry.portfolioConfiguration, locale);
                return (
                  <ButtonMaster
                    key={i}
                    type={entry?.buttonStyle}
                    cta={entry?.typeColor?.color}
                    color={entry?.labelColor?.color}
                    customIcon={entry?.icon?.img}
                    customColor={entry?.iconColor?.color}
                    link={`${routerHandler({
                      typename: entry?.product.product.__typename,
                      slug: [
                        portfolioSlug,
                        entry?.product.product.productTypeSpecific?.slug,
                        entry?.product.product.slug,
                      ],
                      locale: locale,
                    })}`}
                  >
                    {entry?.label || entry?.buttonLabel?.label}
                  </ButtonMaster>
                );
              } else {
                return (
                  <ButtonMaster
                    key={i}
                    type={entry?.buttonStyle}
                    cta={entry?.typeColor?.color}
                    color={entry?.labelColor?.color}
                    customIcon={entry?.icon?.img}
                    customColor={entry?.iconColor?.color}
                    link={routerHandler({
                      typename:
                        entry?.page?.__typename === "ArticleRecord"
                          ? "ArticleRecord"
                          : entry?.__typename,
                      slug: pageRelatedLink(entry?.page, locale),
                      locale: locale,
                    })}
                  >
                    {entry?.label || entry?.buttonLabel?.label}
                  </ButtonMaster>
                );
              }
            })}
          </>
        )}
      </div>
    </header>
  );
}
